/**
 * Update Budget Dialog Component
 *
 * Allows users to update (increase OR decrease) the budget for a running strategy.
 * The new total budget must be greater than the already spent amount.
 * Budget can be decreased but not below the spent amount.
 * Shows live orderbook with budget coverage highlighting.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Grid,
  Chip,
} from '@mui/material';
import type { StrategyListItemV2 } from '../../types/strategy';
import { traderTheme as theme } from './traderDashboardTheme';
import { getExchangeLogoByName, getExchangeIndexByName, EXCHANGE_NAMES } from '../../utils/exchangeUrls';
import { usePolledOrderbook } from '../../hooks/usePolledOrderbook';
import { usePriceUpdates } from '../../hooks/usePriceUpdates';
import { useMarkPrice } from '../../hooks/useMarkPrice';
import { getUsdtKrwRate } from '../../utils/currencyConversion';
import {
  SpotOrderbookCard,
  FuturesOrderbookCard,
  calculateBudgetCoverage,
} from './OrderbookCards';

/** Selected strategy info for bulk display */
export interface BulkStrategyDisplayInfo {
  strategyId: string;
  symbol: string;
  exchangeName: string;
  /** Current budget in USDT */
  budget?: number;
  /** Current profit tiers */
  profitTiers?: { profit_percentage: number; volume_percentage_to_sell: number }[];
  /** Current entry difference ratio (e.g. -0.5 for -0.5%) */
  entryDiff?: number;
}

export interface UpdateBudgetDialogProps {
  open: boolean;
  strategy: StrategyListItemV2 | null;
  onClose: () => void;
  onConfirm: (totalBudgetUsdt: number, entryDifferenceRatio?: number) => void;
  loading: boolean;
  /** Optional custom title for bulk operations */
  title?: string;
  /** Selected strategies for bulk operations - displays exchange logos and symbols */
  selectedStrategies?: BulkStrategyDisplayInfo[];
}

/**
 * Calculate current total budget from strategy execution context
 */
function getCurrentBudget(strategy: StrategyListItemV2 | null): number {
  if (!strategy) return 0;
  return strategy.budget_usdt ?? 0;
}

/**
 * Calculate already spent budget from executed orders
 */
function getSpentBudget(strategy: StrategyListItemV2 | null): number {
  if (!strategy) return 0;
  return (strategy.spot_avg_price_usdt ?? 0) * strategy.spot_total_volume;
}

export const UpdateBudgetDialog: React.FC<UpdateBudgetDialogProps> = ({
  open,
  strategy,
  onClose,
  onConfirm,
  loading,
  title,
  selectedStrategies,
}) => {
  const { t } = useTranslation();
  const currentBudget = getCurrentBudget(strategy);
  const spentBudget = getSpentBudget(strategy);
  const remainingBudget = currentBudget - spentBudget;

  // State for new budget input
  const [newBudgetInput, setNewBudgetInput] = useState('');
  const [entryDiffRatio, setEntryDiffRatio] = useState<number>(-0.5); // Default -0.5%
  const [useCurrent, setUseCurrent] = useState(false); // Sync with live difference

  // Reset state when dialog opens
  useEffect(() => {
    if (open && strategy) {
      // Default to current budget (user can increase or decrease)
      setNewBudgetInput(String(Math.ceil(currentBudget)));
      // Get current entry threshold from opportunity
      // Field reuse in BuyOpen strategies:
      //   - quote_conversion_rate stores the entry_threshold_ratio (entry condition threshold)
      //   - max_profitable_volume stores the budget_usdt (total USDT budget)
      // See: crates/arbi-engine/src/executors/strategy/states/buyopen/mod.rs lines 128-139
      const currentRatio = strategy.entry_diff_ratio;
      if (currentRatio !== undefined && currentRatio !== null) {
        setEntryDiffRatio(currentRatio * 100); // Convert to percentage
      } else {
        setEntryDiffRatio(-0.5);
      }
      setUseCurrent(false); // Reset sync mode
    }
  }, [open, strategy, currentBudget]);

  // Get exchange info from strategy - with multiple fallback sources for BuyOpen state
  // Priority: 1) opportunity.buy_exchange (number), 2) order.exchange (string), 3) quote currency inference
  const spotExchangeIndex = useMemo(() => {
    if (!strategy) {
      console.log('[UpdateBudgetDialog] No strategy, defaulting to Upbit (0)');
      return 0; // Default to Upbit for StandbyHedged strategies
    }

    // Debug: Log the raw strategy data to understand what we're receiving
    console.log('[UpdateBudgetDialog] Raw strategy data:', {
      strategy_type: strategy.strategy_type,
      current_state: strategy.current_state,
      spot_exchange_name: strategy.spot_exchange_name,
      buy_quote: strategy.buy_quote,
      symbol: strategy.symbol,
    });

    const idx = getExchangeIndexByName(strategy.spot_exchange_name);
    if (idx !== undefined) {
      console.log(`[UpdateBudgetDialog] Using strategy.spot_exchange_name: "${strategy.spot_exchange_name}" -> ${idx}`);
      return idx;
    }

    const quote = strategy.buy_quote;
    if (quote === 'KRW' || quote === 'K') {
      // KRW quote likely means Upbit (most common Korean exchange)
      console.log(`[UpdateBudgetDialog] Inferred from KRW quote: defaulting to Upbit (0)`);
      return 0;
    }

    // Default: Upbit for StandbyHedged strategies (most common use case)
    console.log('[UpdateBudgetDialog] No exchange found, defaulting to Upbit (0)');
    return 0;
  }, [strategy]);

  // Map exchange index to exchange name for REST API (same as StandbyHedgedConfirmDialog)
  // Must match EXCHANGE_NAMES array in exchangeUrls.ts
  const spotExchangeName = useMemo(() => {
    const name = (() => {
      switch (spotExchangeIndex) {
        case 0: return 'upbit';
        case 1: return 'bithumb';
        case 2: return 'coinone';
        case 3: return 'binance';
        case 4: return 'htx';
        case 5: return 'gateio';
        case 6: return 'kucoin';
        case 7: return 'okx';
        case 8: return 'mexc';
        case 9: return 'bybit';
        case 10: return 'bitget';
        default: return 'binance';
      }
    })();
    console.log(`[UpdateBudgetDialog] spotExchangeName: ${name} (from index ${spotExchangeIndex})`);
    return name;
  }, [spotExchangeIndex]);

  // Get quote currency from strategy
  // buy_quote can be single char ('K','U','B') from binary protocol or full string ('KRW','USDT','BTC') from JSON API
  const quoteCurrency = useMemo((): 'K' | 'U' | 'B' => {
    if (!strategy) return 'U';
    const quote = strategy.buy_quote;
    if (quote === 'K' || quote === 'KRW') return 'K';
    if (quote === 'B' || quote === 'BTC') return 'B';
    return 'U';
  }, [strategy]);

  // Get base asset from strategy
  const baseAsset = strategy?.symbol || 'BTC';

  // Convert quote currency for hook (EXACT same as StandbyHedgedConfirmDialog line 114)
  const spotQuoteCurrency = quoteCurrency === 'K' ? 'KRW' : quoteCurrency === 'B' ? 'BTC' : 'USDT';

  // CCXT uses unified symbol format: "BTC/USDT", "BTC/KRW" (EXACT same as StandbyHedgedConfirmDialog lines 117-118)
  const futuresCcxtSymbol = `${baseAsset}/USDT`;
  const spotCcxtSymbol = `${baseAsset}/${spotQuoteCurrency}`;

  // Debug: log all values being passed to hooks (only when dialog is open)
  useEffect(() => {
    if (open) {
      console.log('[UpdateBudgetDialog] Hook params:', {
        futuresExchange: 'binanceusdm',
        futuresSymbol: futuresCcxtSymbol,
        spotExchange: spotExchangeName,
        spotSymbol: spotCcxtSymbol,
        enabled: open,
        quoteCurrency,
        spotQuoteCurrency,
        baseAsset,
        spotExchangeIndex,
      });
    }
  }, [open, futuresCcxtSymbol, spotExchangeName, spotCcxtSymbol, quoteCurrency, spotQuoteCurrency, baseAsset, spotExchangeIndex]);

  // Poll Binance Futures orderbook via CCXT REST (EXACT same as StandbyHedgedConfirmDialog lines 121-126)
  const futuresState = usePolledOrderbook({
    exchange: 'binanceusdm',
    symbol: futuresCcxtSymbol,
    enabled: open,
    isFutures: true,
  });

  // Poll spot exchange orderbook via CCXT REST (EXACT same as StandbyHedgedConfirmDialog lines 132-137)
  const spotState = usePolledOrderbook({
    exchange: spotExchangeName,
    symbol: spotCcxtSymbol,
    enabled: open,
    isFutures: false,
  });

  // Get mark price data for funding rate
  const markPriceData = useMarkPrice(futuresCcxtSymbol);

  // Get price data for conversions
  const priceData = usePriceUpdates();
  const krwToUsdtRate = useMemo(() => getUsdtKrwRate(priceData), [priceData]);
  const btcUsdtRate = priceData?.binanceBtcUsdt ?? null;

  // Parse and validate new budget (must be defined before spotBudgetInQuote)
  const newBudget = useMemo(() => {
    const parsed = parseFloat(newBudgetInput);
    return isNaN(parsed) ? 0 : parsed;
  }, [newBudgetInput]);

  // Calculate budget in quote currency for spot
  const spotBudgetInQuote = useMemo(() => {
    if (newBudget < 10) return 0;
    if (quoteCurrency === 'K' && krwToUsdtRate) {
      return newBudget * krwToUsdtRate;
    }
    if (quoteCurrency === 'B' && btcUsdtRate) {
      return newBudget / btcUsdtRate;
    }
    return newBudget;
  }, [newBudget, quoteCurrency, krwToUsdtRate, btcUsdtRate]);

  // Sorted orderbook data
  const sortedFuturesBids = useMemo(() => {
    return [...(futuresState.bids || [])].sort((a, b) => b.price - a.price);
  }, [futuresState.bids]);

  const sortedSpotAsks = useMemo(() => {
    return [...(spotState.asks || [])].sort((a, b) => a.price - b.price);
  }, [spotState.asks]);

  // Calculate budget coverage
  const futuresCoverage = useMemo(() => {
    return calculateBudgetCoverage(sortedFuturesBids, newBudget);
  }, [sortedFuturesBids, newBudget]);

  const spotCoverage = useMemo(() => {
    return calculateBudgetCoverage(sortedSpotAsks, spotBudgetInQuote);
  }, [sortedSpotAsks, spotBudgetInQuote]);

  // Calculate differential percentage
  const diffPercent = useMemo(() => {
    const futuresAvgBid = futuresCoverage.avgPrice;
    const spotAvgAsk = spotCoverage.avgPrice;
    if (!futuresAvgBid || !spotAvgAsk) return null;

    let spotAvgInUsdt = spotAvgAsk;
    if (quoteCurrency === 'K' && krwToUsdtRate) {
      spotAvgInUsdt = spotAvgAsk / krwToUsdtRate;
    } else if (quoteCurrency === 'B' && btcUsdtRate) {
      spotAvgInUsdt = spotAvgAsk * btcUsdtRate;
    }
    if (spotAvgInUsdt <= 0) return null;
    return ((spotAvgInUsdt - futuresAvgBid) / spotAvgInUsdt) * 100;
  }, [futuresCoverage.avgPrice, spotCoverage.avgPrice, quoteCurrency, krwToUsdtRate, btcUsdtRate]);

  // Sync entry diff ratio with current difference when useCurrent is enabled
  useEffect(() => {
    if (useCurrent && diffPercent !== null) {
      setEntryDiffRatio(parseFloat(diffPercent.toFixed(2)));
    }
  }, [useCurrent, diffPercent]);

  // Calculate budget change (positive = increase, negative = decrease)
  const budgetChange = newBudget - currentBudget;

  // Minimum notional value (USDT)
  const MIN_NOTIONAL = 6;

  // Calculate remaining budget after spent
  const remainingAfterUpdate = newBudget - spentBudget;

  // Bulk mode flag
  const isBulkMode = !strategy && !!title;

  // Validation: different rules for single vs bulk mode
  const isValid = isBulkMode
    ? newBudget >= MIN_NOTIONAL // Bulk mode: just need valid budget amount
    : (newBudget > spentBudget && newBudget !== currentBudget && remainingAfterUpdate >= MIN_NOTIONAL);

  const errorMessage = useMemo(() => {
    if (!newBudgetInput) return null;
    if (newBudget <= 0) return t('traderDashboard.updateBudget.errorInvalidAmount');

    // Bulk mode: only check minimum
    if (isBulkMode) {
      if (newBudget < MIN_NOTIONAL) {
        return t('traderDashboard.updateBudget.errorBelowMinNotional', {
          remaining: newBudget.toFixed(2),
          min: MIN_NOTIONAL
        });
      }
      return null;
    }

    // Single strategy mode: full validation
    if (newBudget <= spentBudget) {
      return t('traderDashboard.updateBudget.errorBelowSpent', { spent: spentBudget.toFixed(2) });
    }
    if (remainingAfterUpdate < MIN_NOTIONAL) {
      return t('traderDashboard.updateBudget.errorBelowMinNotional', {
        remaining: remainingAfterUpdate.toFixed(2),
        min: MIN_NOTIONAL
      });
    }
    if (newBudget === currentBudget) {
      return t('traderDashboard.updateBudget.errorNoChange');
    }
    return null;
  }, [newBudgetInput, newBudget, currentBudget, spentBudget, remainingAfterUpdate, isBulkMode, t]);

  const handleConfirm = () => {
    if (!isValid) return;
    // Convert entry diff ratio from percentage to decimal
    const ratioDecimal = entryDiffRatio / 100;
    onConfirm(newBudget, ratioDecimal);
  };

  // For single-strategy mode, require strategy; for bulk mode (title provided), allow null
  if (!strategy && !title) return null;

  const symbol = strategy?.symbol || '-';

  // Get exchange display name
  const exchangeDisplayName = EXCHANGE_NAMES[spotExchangeIndex]?.charAt(0).toUpperCase() +
    EXCHANGE_NAMES[spotExchangeIndex]?.slice(1) || 'Exchange';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          backgroundColor: theme.bg.primary,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.accent,
        }}
      >
        <Stack direction="column" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {strategy && (
              <Avatar
                src={getExchangeLogoByName(spotExchangeName)}
                sx={{ width: 28, height: 28 }}
              />
            )}
            <Typography variant="h6" fontWeight="bold" sx={{ color: theme.accent }}>
              {title || t('traderDashboard.updateBudget.title', { symbol })}
            </Typography>
          </Stack>
          {/* Bulk mode: show selected strategies with exchange logos, symbols, and budget */}
          {selectedStrategies && selectedStrategies.length > 0 && (
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              mt: 1,
              p: 1.5,
              backgroundColor: theme.bg.secondary,
              borderRadius: 1,
              maxHeight: 150,
              overflowY: 'auto',
            }}>
              {selectedStrategies.map((s) => (
                <Box
                  key={s.strategyId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    backgroundColor: theme.bg.hover,
                    borderRadius: 1,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <Avatar
                    src={getExchangeLogoByName(s.exchangeName)}
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography variant="caption" sx={{ color: theme.text.primary, fontWeight: 500 }}>
                    {s.symbol}
                  </Typography>
                  {s.budget !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.text.secondary,
                        fontFamily: 'monospace',
                        ml: 0.5,
                      }}
                    >
                      ${s.budget.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </Typography>
                  )}
                  {s.entryDiff !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: s.entryDiff < 0 ? theme.profit : theme.warning,
                        fontFamily: 'monospace',
                        ml: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      ({s.entryDiff.toFixed(2)}%)
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.bg.primary, pt: 3 }}>
        {/* Current Budget Info - only show for single strategy mode */}
        {strategy && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 1 }}>
              {t('traderDashboard.updateBudget.currentBudgetInfo')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                  {t('traderDashboard.updateBudget.totalBudget')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.text.primary, fontFamily: 'monospace', fontWeight: 600 }}>
                  ${currentBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                  {t('traderDashboard.updateBudget.spent')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.warning, fontFamily: 'monospace', fontWeight: 600 }}>
                  ${spentBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                  {t('traderDashboard.updateBudget.remaining')}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.profit, fontFamily: 'monospace', fontWeight: 600 }}>
                  ${remainingBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* First Row: New Budget Input */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: theme.text.primary, mb: 1, fontWeight: 600 }}>
            {t('traderDashboard.updateBudget.newTotalBudget')}
          </Typography>

          <TextField
            fullWidth
            type="number"
            value={newBudgetInput}
            onChange={(e) => setNewBudgetInput(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.bg.secondary,
                '& fieldset': {
                  borderColor: theme.border,
                },
                '&:hover fieldset': {
                  borderColor: theme.accent,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.accent,
                },
              },
              '& .MuiOutlinedInput-input': {
                color: theme.text.primary,
                fontFamily: 'monospace',
                textAlign: 'right',
              },
            }}
          />
          {krwToUsdtRate && (
            <Typography variant="caption" sx={{ color: theme.text.secondary, mt: 0.5, display: 'block', fontFamily: 'monospace', textAlign: 'right' }}>
              ≈ ₩{(newBudget * krwToUsdtRate).toLocaleString('ko-KR', { maximumFractionDigits: 0 })}
            </Typography>
          )}
          {/* Budget change indicator - only show for single strategy mode */}
          {!isBulkMode && isValid && budgetChange !== 0 && (
            <Typography
              variant="body2"
              sx={{
                color: budgetChange > 0 ? theme.profit : theme.warning,
                mt: 1,
                fontFamily: 'monospace',
              }}
            >
              {budgetChange > 0 ? '+' : ''}${budgetChange.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {budgetChange > 0 ? t('traderDashboard.updateBudget.increase') : t('traderDashboard.updateBudget.decrease')}
            </Typography>
          )}
        </Box>

        {/* Second Row: Entry Threshold */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: theme.text.primary, mb: 1, fontWeight: 600 }}>
            {t('traderDashboard.updateBudget.entryThreshold')}
          </Typography>
          <Typography variant="caption" sx={{ color: theme.text.secondary, display: 'block', mb: 2 }}>
            {t('traderDashboard.updateBudget.entryThresholdDesc')}
          </Typography>

          {/* Entry threshold input and use current toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Manual input */}
            <TextField
              size="small"
              type="number"
              value={useCurrent ? (diffPercent?.toFixed(2) ?? entryDiffRatio) : entryDiffRatio}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                if (!isNaN(value)) {
                  setUseCurrent(false); // Disable sync when manually editing
                  setEntryDiffRatio(value);
                }
              }}
              disabled={useCurrent}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{
                step: 0.01,
              }}
              sx={{
                flex: 1,
                minWidth: 200,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: useCurrent ? theme.bg.hover : theme.bg.secondary,
                  '& fieldset': { borderColor: useCurrent ? theme.accent : theme.border },
                  '&:hover fieldset': { borderColor: theme.accent },
                },
                '& .MuiInputBase-input': {
                  color: theme.text.primary,
                  fontFamily: 'monospace',
                  textAlign: 'right',
                },
                '& .MuiInputAdornment-root': { color: theme.text.secondary },
              }}
            />

            {/* Use Current Toggle Chip - only available in single strategy mode (not bulk) */}
            {!isBulkMode && diffPercent !== null && (
              <Chip
                label={useCurrent
                  ? `${t('traderDashboard.updateBudget.syncing', 'Syncing')}: ${diffPercent.toFixed(2)}%`
                  : t('traderDashboard.updateBudget.useCurrentDiff', 'Use Current')
                }
                onClick={() => {
                  if (!useCurrent) {
                    setUseCurrent(true);
                    setEntryDiffRatio(parseFloat(diffPercent.toFixed(2)));
                  } else {
                    setUseCurrent(false);
                  }
                }}
                size="small"
                sx={{
                  backgroundColor: useCurrent
                    ? (diffPercent < 0 ? 'rgba(63, 185, 80, 0.3)' : 'rgba(210, 153, 34, 0.3)')
                    : 'transparent',
                  color: diffPercent < 0 ? theme.profit : theme.warning,
                  border: `1px solid ${diffPercent < 0 ? theme.profit : theme.warning}`,
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: diffPercent < 0 ? 'rgba(63, 185, 80, 0.2)' : 'rgba(210, 153, 34, 0.2)',
                  },
                }}
              />
            )}
          </Box>

          {/* Status text */}
          <Typography
            variant="body2"
            sx={{
              color: entryDiffRatio < 0 ? theme.profit : theme.warning,
              mt: 1,
              fontFamily: 'monospace',
            }}
          >
            {entryDiffRatio < 0
              ? t('traderDashboard.updateBudget.spotCheaper', { percent: Math.abs(entryDiffRatio).toFixed(2) })
              : entryDiffRatio > 0
                ? t('traderDashboard.updateBudget.spotExpensive', { percent: entryDiffRatio.toFixed(2) })
                : t('traderDashboard.updateBudget.breakEven')}
            {useCurrent && (
              <Typography component="span" sx={{ color: theme.accent, ml: 1, fontSize: '0.75rem' }}>
                ({t('traderDashboard.updateBudget.liveSync', 'live sync')})
              </Typography>
            )}
          </Typography>
        </Box>



        {/* Info Alert - only show for single strategy mode */}
        {strategy && (
          <Alert severity="info" sx={{ backgroundColor: 'rgba(88, 166, 255, 0.1)', color: theme.accent, mb: 3 }}>
            {t('traderDashboard.updateBudget.info')}
          </Alert>
        )}

        {/* Orderbook Display - only show for single strategy mode, not bulk */}
        {strategy && (
          <Grid container spacing={2}>
            {/* Futures Orderbook (Left) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FuturesOrderbookCard
                bids={sortedFuturesBids}
                loading={futuresState.loading && sortedFuturesBids.length === 0}
                error={futuresState.error}
                fundingRate={markPriceData?.fundingRate ?? null}
                nextFundingTime={markPriceData?.nextFundingTime ?? null}
                avgPrice={futuresCoverage.avgPrice}
                highlightCount={futuresCoverage.highlightCount}
                compact
              />
            </Grid>

            {/* Spot Orderbook (Right) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <SpotOrderbookCard
                exchangeName={exchangeDisplayName}
                exchangeIndex={spotExchangeIndex}
                asks={sortedSpotAsks}
                loading={spotState.loading && sortedSpotAsks.length === 0}
                error={spotState.error}
                quoteCurrency={quoteCurrency}
                diffPercent={diffPercent}
                avgPrice={spotCoverage.avgPrice}
                highlightCount={spotCoverage.highlightCount}
                quoteToUsdtRate={quoteCurrency === 'K' ? krwToUsdtRate : quoteCurrency === 'B' ? btcUsdtRate : null}
                compact
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          backgroundColor: theme.bg.primary,
          borderTop: `1px solid ${theme.border}`,
          p: 2,
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.text.secondary }} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !isValid}
          startIcon={loading ? <CircularProgress size={16} sx={{ color: theme.text.primary }} /> : null}
          sx={{
            backgroundColor: isBulkMode ? theme.accent : (budgetChange > 0 ? theme.profit : theme.warning),
            color: theme.text.primary,
            '&:hover': {
              backgroundColor: isBulkMode ? '#4b8fd9' : (budgetChange > 0 ? '#2ea043' : '#d29922'),
            },
            '&.Mui-disabled': {
              backgroundColor: theme.bg.secondary,
              color: theme.text.secondary,
            },
          }}
        >
          {loading ? t('traderDashboard.updateBudget.updating') : t('traderDashboard.updateBudget.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateBudgetDialog;
